import { AttachmentReference, CardReference, CommentReference, GroupedMap, GroupedMap2D, Statistics, Timing, Total } from 'app/trello/models/board-summary';

export class BoardSummaryFilter {

  public filter(
    statistics: Statistics,
    allowedCardIds: Set<string>,
  ): Statistics {

    const filteredCard = this.filterCard(statistics.card, allowedCardIds);
    const filteredLabel = this.filterLabel(statistics.label, allowedCardIds);
    const filteredList = this.filterList(statistics.list, allowedCardIds);
    const filteredMember = this.filterMember(statistics.member, allowedCardIds);
    const filteredStage = this.filterStage(statistics.stage, allowedCardIds);
    const filteredAttachment = this.filterAttachment(statistics.attachment, allowedCardIds);
    const filteredComment = this.filterComment(statistics.comment, allowedCardIds);
    const filteredOverdue = this.filterOverdue(statistics.overdue, allowedCardIds)
    const filteredTimings = this.filterTimings(statistics.timings, allowedCardIds);

    return {
      card: filteredCard,
      label: filteredLabel,
      list: filteredList,
      member: filteredMember,
      stage: filteredStage,
      attachment: filteredAttachment,
      comment: filteredComment,
      overdue: filteredOverdue,
      timings: filteredTimings,
    } as Statistics;
  }

  private filterCard(
    card: Statistics['card'],
    allowedCardIds: Set<string>,
  ): Statistics['card'] {

    return {
      total: this.filterTotals(card.total, allowedCardIds),
      duration: this.filterGroupedDurations(card.duration, allowedCardIds),
      created: this.filterGroupedReferences2D(card.created, allowedCardIds),
      closed: this.filterGroupedReferences2D(card.closed, allowedCardIds),
    };
  }

  private filterLabel(
    label: Statistics['label'],
    allowedCardIds: Set<string>,
  ): Statistics['label'] {

    return {
      total: this.filterTotals(label.total, allowedCardIds),
      duration: this.filterGroupedDurations(label.duration, allowedCardIds),
      added: this.filterGroupedReferences2D(label.added, allowedCardIds),
      removed: this.filterGroupedReferences2D(label.removed, allowedCardIds),
    };
  }

  private filterList(
    list: Statistics['list'],
    allowedCardIds: Set<string>,
  ): Statistics['list'] {

    return {
      total: this.filterTotals(list.total, allowedCardIds),
      duration: this.filterGroupedDurations(list.duration, allowedCardIds),
      movedIn: this.filterGroupedReferences2D(list.movedIn, allowedCardIds),
      movedOut: this.filterGroupedReferences2D(list.movedOut, allowedCardIds),
    };
  }

  private filterMember(
    member: Statistics['member'],
    allowedCardIds: Set<string>,
  ): Statistics['member'] {

    return {
      total: this.filterTotals(member.total, allowedCardIds),
      duration: this.filterGroupedDurations(member.duration, allowedCardIds),
      assigned: this.filterGroupedReferences2D(member.assigned, allowedCardIds),
      unassigned: this.filterGroupedReferences2D(member.unassigned, allowedCardIds),
    };
  }

  private filterStage(
    stage: Statistics['stage'],
    allowedCardIds: Set<string>,
  ): Statistics['stage'] {

    return {
      total: this.filterTotals(stage.total, allowedCardIds),
      duration: this.filterGroupedDurations(stage.duration, allowedCardIds),
      movedIn: this.filterGroupedReferences2D(stage.movedIn, allowedCardIds),
      movedOut: this.filterGroupedReferences2D(stage.movedOut, allowedCardIds),
    };
  }

  private filterAttachment(
    attachment: Statistics['attachment'],
    allowedCardIds: Set<string>,
  ): Statistics['attachment'] {

    return {
      total: this.filterTotals(attachment.total, allowedCardIds),
      added: this.filterGroupedReferences2D(attachment.added, allowedCardIds),
      removed: this.filterGroupedReferences2D(attachment.removed, allowedCardIds),
    };
  }

  private filterComment(
    comment: Statistics['comment'],
    allowedCardIds: Set<string>,
  ): Statistics['comment'] {

    return {
      total: this.filterTotals(comment.total, allowedCardIds),
      added: this.filterGroupedReferences2D(comment.added, allowedCardIds),
      removed: this.filterGroupedReferences2D(comment.removed, allowedCardIds),
    };
  }

  private filterOverdue(
    overdue: Statistics['overdue'],
    allowedCardIds: Set<string>,
  ): Statistics['overdue'] {

    return {
      total: this.filterTotals(overdue.total, allowedCardIds),
      duration: this.filterGroupedDurations(overdue.duration, allowedCardIds),
      becameOverdue: this.filterGroupedReferences(overdue.becameOverdue, allowedCardIds),
    };
  }

  private filterTimings(
    timings: Statistics['timings'],
    allowedCardIds: Set<string>,
  ): Statistics['timings'] {

    const filteredTimings: Record<string, Timing> = {};

    for (const cardId of Object.keys(timings)) {

      if (allowedCardIds.has(cardId)) {
        filteredTimings[cardId] = timings[cardId];
      }
    }

    return filteredTimings;
  }

  private filterTotals<R extends CardReference | CommentReference | AttachmentReference>(
    total: Total<R>,
    allowedCardIds: Set<string>,
  ): Total<R> {

    const filteredStart = total.start.filter(reference => allowedCardIds.has(this.idFromReference(reference)));
    const filteredEnd = total.end.filter(reference => allowedCardIds.has(this.idFromReference(reference)));

    const filteredDay: { [date: string]: typeof total.day[string] } = {};

    for (const date of Object.keys(total.day)) {

      const dayCounts = total.day[date];

      const filteredDayCounts = dayCounts.filter(reference => allowedCardIds.has(this.idFromReference(reference)));

      if (filteredDayCounts.length > 0) {
        filteredDay[date] = filteredDayCounts;
      }
    }

    return {
      start: filteredStart,
      end: filteredEnd,
      day: filteredDay,
    }
  }

  private filterGroupedDurations(
    duration: GroupedMap2D<number>,
    allowedCardIds: Set<string>,
  ): GroupedMap2D<number> {

    const filteredDuration: GroupedMap2D<number> = {};

    for (const date of Object.keys(duration)) {

      const cardDurations = duration[date];

      const filteredCardDurations: { [cardId: string]: number } = {};

      for (const cardId of Object.keys(cardDurations)) {

        if (allowedCardIds.has(cardId)) {
          filteredCardDurations[cardId] = cardDurations[cardId];
        }
      }

      if (Object.keys(filteredCardDurations).length > 0) {
        filteredDuration[date] = filteredCardDurations;
      }
    }

    return filteredDuration;
  }

  private filterGroupedReferences<R extends CardReference | CommentReference | AttachmentReference>(
    groups: GroupedMap<R[]>,
    allowedCardIds: Set<string>,
  ): GroupedMap<R[]> {

    const filteredGroups: GroupedMap<R[]> = {};

    for (const date of Object.keys(groups)) {

      const references = groups[date];

      const filteredReferences = references.filter(reference => allowedCardIds.has(this.idFromReference(reference)));

      if (filteredReferences.length > 0) {
        filteredGroups[date] = filteredReferences;
      }
    }

    return filteredGroups;
  }

  private filterGroupedReferences2D<R extends CardReference | CommentReference | AttachmentReference>(
    groups: GroupedMap2D<R[]>,
    allowedCardIds: Set<string>,
  ): GroupedMap2D<R[]> {

    const filteredGroups: GroupedMap2D<R[]> = {};

    for (const group of Object.keys(groups)) {

      const subGroup = groups[group];

      const filteredSubGroup: { [subKey: string]: R[] } = {};

      for (const subKey of Object.keys(subGroup)) {

        const references = subGroup[subKey];

        const filteredReferences = references.filter(reference => allowedCardIds.has(this.idFromReference(reference)));

        if (filteredReferences.length > 0) {
          filteredSubGroup[subKey] = filteredReferences;
        }
      }

      if (Object.keys(filteredSubGroup).length > 0) {
        filteredGroups[group] = filteredSubGroup;
      }
    }

    return filteredGroups;
  }

  private idFromReference(
    reference: CardReference | CommentReference | AttachmentReference,
  ): string {

    if ('cardId' in reference) {
      return reference.cardId;

    } else if ('commentId' in reference) {
      return reference.commentId.split('-')[0]; // remove comment element suffix

    } else if ('attachmentId' in reference) {
      return reference.attachmentId.split('-')[0]; // remove comment element suffix

    } else {
      return '';
    }
  }

}
